/**
 * Global State Store using Zustand
 * Manages authentication, user preferences, and app-wide state
 */
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Auth Store - Manages user authentication state
export const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isLoading: true,
      isAuthenticated: false,

      // Set user data
      setUser: (user) => set({ user, isAuthenticated: !!user }),

      // Set token (kept for backward compat, cookie is primary)
      setToken: (token) => {
        set({ token });
      },

      // Check authentication status
      checkAuth: async () => {
        set({ isLoading: true });
        try {
          const response = await axios.get(`${API}/auth/me`, {
            withCredentials: true,
          });
          
          set({ 
            user: response.data, 
            isAuthenticated: true,
            isLoading: false 
          });
          return response.data;
        } catch (error) {
          set({ 
            user: null, 
            token: null,
            isAuthenticated: false,
            isLoading: false 
          });
          return null;
        }
      },

      // Login
      login: async (email, password) => {
        const response = await axios.post(`${API}/auth/login`, 
          { email, password },
          { withCredentials: true }
        );
        
        set({ 
          user: response.data, 
          token: response.data.token || null,
          isAuthenticated: true 
        });
        
        return response.data;
      },

      // Register
      register: async (name, email, password) => {
        const response = await axios.post(`${API}/auth/register`,
          { name, email, password },
          { withCredentials: true }
        );
        
        set({ 
          user: response.data, 
          token: response.data.token || null,
          isAuthenticated: true 
        });
        
        return response.data;
      },

      // Logout
      logout: async () => {
        try {
          await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
        } catch (error) {
          // Logout errors are non-critical
        }
        set({ 
          user: null, 
          token: null,
          isAuthenticated: false 
        });
      },

      // Update user profile
      updateUser: (updates) => {
        const currentUser = get().user;
        if (currentUser) {
          set({ user: { ...currentUser, ...updates } });
        }
      },
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: () => ({}),
    }
  )
);


// Google Drive Store - Manages Drive connection state
export const useDriveStore = create((set, get) => ({
  isConnected: false,
  isLoading: false,
  files: [],
  error: null,

  // Check Drive connection status
  checkStatus: async (userId) => {
    if (!userId) return;
    
    set({ isLoading: true, error: null });
    try {
      const response = await axios.get(`${API}/drive/status`, {
        params: { user_id: userId },
        withCredentials: true,
      });
      set({ 
        isConnected: response.data.connected,
        isLoading: false 
      });
    } catch (error) {
      set({ 
        isConnected: false, 
        isLoading: false,
        error: error.message 
      });
    }
  },

  // Initiate Drive connection
  connect: async (userId) => {
    try {
      const response = await axios.get(`${API}/drive/connect`, {
        params: { user_id: userId },
        withCredentials: true,
      });
      
      // Redirect to Google OAuth
      if (response.data.authorization_url) {
        window.location.href = response.data.authorization_url;
      }
    } catch (error) {
      set({ error: error.response?.data?.detail || error.message });
      throw error;
    }
  },

  // Disconnect Drive
  disconnect: async (userId) => {
    set({ isLoading: true });
    try {
      await axios.delete(`${API}/drive/disconnect`, {
        params: { user_id: userId },
        withCredentials: true,
      });
      set({ 
        isConnected: false, 
        files: [],
        isLoading: false 
      });
    } catch (error) {
      set({ 
        isLoading: false,
        error: error.response?.data?.detail || error.message 
      });
      throw error;
    }
  },

  // List files
  listFiles: async (userId, folderId = null) => {
    set({ isLoading: true, error: null });
    try {
      const response = await axios.get(`${API}/drive/files`, {
        params: { user_id: userId, folder_id: folderId },
        withCredentials: true,
      });
      set({ 
        files: response.data.files,
        isLoading: false 
      });
      return response.data.files;
    } catch (error) {
      set({ 
        isLoading: false,
        error: error.response?.data?.detail || error.message 
      });
      throw error;
    }
  },

  // Upload file
  uploadFile: async (userId, file, folderId = null) => {
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/drive/upload`, formData, {
        params: { user_id: userId, folder_id: folderId },
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    } catch (error) {
      set({ error: error.response?.data?.detail || error.message });
      throw error;
    }
  },

  // Clear error
  clearError: () => set({ error: null }),
}));


// UI Store - Manages UI state like modals, toasts, theme
export const useUIStore = create(
  persist(
    (set) => ({
      theme: 'dark',
      sidebarOpen: false,
      activeModal: null,
      
      // Toggle theme
      toggleTheme: () => set((state) => ({ 
        theme: state.theme === 'dark' ? 'light' : 'dark' 
      })),
      
      // Set theme
      setTheme: (theme) => set({ theme }),
      
      // Toggle sidebar
      toggleSidebar: () => set((state) => ({ 
        sidebarOpen: !state.sidebarOpen 
      })),
      
      // Open modal
      openModal: (modalId) => set({ activeModal: modalId }),
      
      // Close modal
      closeModal: () => set({ activeModal: null }),
    }),
    {
      name: 'ui-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ theme: state.theme }),
    }
  )
);


// Trips Store - Manages roadtrip planning state
export const useTripsStore = create((set, get) => ({
  destinations: [
    { id: 1, city: '', country: '' },
    { id: 2, city: '', country: '' },
  ],
  sights: [],
  isLoading: false,
  routeGenerated: false,

  // Set destinations
  setDestinations: (destinations) => set({ destinations }),

  // Add destination
  addDestination: () => {
    const destinations = get().destinations;
    const newId = Math.max(...destinations.map(d => d.id)) + 1;
    set({ 
      destinations: [...destinations, { id: newId, city: '', country: '' }] 
    });
  },

  // Remove destination
  removeDestination: (id) => {
    const destinations = get().destinations;
    if (destinations.length <= 2) return;
    set({ 
      destinations: destinations.filter(d => d.id !== id) 
    });
  },

  // Update destination
  updateDestination: (id, field, value) => {
    const destinations = get().destinations;
    set({
      destinations: destinations.map(d => 
        d.id === id ? { ...d, [field]: value } : d
      )
    });
  },

  // Set sights
  setSights: (sights) => set({ sights }),

  // Set loading
  setLoading: (isLoading) => set({ isLoading }),

  // Set route generated
  setRouteGenerated: (generated) => set({ routeGenerated: generated }),

  // Reset trip
  resetTrip: () => set({
    destinations: [
      { id: 1, city: '', country: '' },
      { id: 2, city: '', country: '' },
    ],
    sights: [],
    routeGenerated: false,
  }),
}));


// Budget Store - Manages budget tracking state
export const useBudgetStore = create((set, get) => ({
  expenses: [],
  notes: [],
  totalByCategory: {},
  totalByCurrency: {},
  isLoading: false,

  // Set expenses
  setExpenses: (expenses) => {
    // Calculate totals
    const totalByCategory = {};
    const totalByCurrency = {};
    
    expenses.forEach(exp => {
      const cat = exp.category || 'other';
      const curr = exp.currency || 'EUR';
      const amount = parseFloat(exp.amount) || 0;
      
      totalByCategory[cat] = (totalByCategory[cat] || 0) + amount;
      totalByCurrency[curr] = (totalByCurrency[curr] || 0) + amount;
    });
    
    set({ expenses, totalByCategory, totalByCurrency });
  },

  // Set notes
  setNotes: (notes) => set({ notes }),

  // Set loading
  setLoading: (isLoading) => set({ isLoading }),

  // Add expense locally
  addExpense: (expense) => {
    const expenses = [...get().expenses, expense];
    get().setExpenses(expenses);
  },

  // Remove expense locally
  removeExpense: (expenseId) => {
    const expenses = get().expenses.filter(e => e.expense_id !== expenseId);
    get().setExpenses(expenses);
  },
}));


// Tickets Store - Manages tickets state
export const useTicketsStore = create((set, get) => ({
  tickets: [],
  isLoading: false,
  error: null,

  // Set tickets
  setTickets: (tickets) => set({ tickets }),

  // Set loading
  setLoading: (isLoading) => set({ isLoading }),

  // Add ticket locally
  addTicket: (ticket) => {
    const tickets = [...get().tickets, ticket];
    set({ tickets });
  },

  // Update ticket locally
  updateTicket: (ticketId, updates) => {
    const tickets = get().tickets.map(t => 
      t.ticket_id === ticketId ? { ...t, ...updates } : t
    );
    set({ tickets });
  },

  // Remove ticket locally
  removeTicket: (ticketId) => {
    const tickets = get().tickets.filter(t => t.ticket_id !== ticketId);
    set({ tickets });
  },

  // Get tickets by trip
  getTicketsByTrip: (tripId) => {
    return get().tickets.filter(t => t.trip_id === tripId);
  },
}));


// Bookings Store - Manages bookings state
export const useBookingsStore = create((set, get) => ({
  bookings: [],
  isLoading: false,
  error: null,

  // Set bookings
  setBookings: (bookings) => set({ bookings }),

  // Set loading
  setLoading: (isLoading) => set({ isLoading }),

  // Add booking locally
  addBooking: (booking) => {
    const bookings = [...get().bookings, booking];
    set({ bookings });
  },

  // Update booking locally
  updateBooking: (bookingId, updates) => {
    const bookings = get().bookings.map(b => 
      b.booking_id === bookingId ? { ...b, ...updates } : b
    );
    set({ bookings });
  },

  // Remove booking locally
  removeBooking: (bookingId) => {
    const bookings = get().bookings.filter(b => b.booking_id !== bookingId);
    set({ bookings });
  },

  // Get bookings by trip
  getBookingsByTrip: (tripId) => {
    return get().bookings.filter(b => b.trip_id === tripId);
  },
}));


// MyTrips Store - Manages saved trips state
export const useMyTripsStore = create((set, get) => ({
  trips: [],
  isLoading: false,
  error: null,

  // Set trips
  setTrips: (trips) => set({ trips }),

  // Set loading
  setLoading: (isLoading) => set({ isLoading }),

  // Add trip locally
  addTrip: (trip) => {
    const trips = [...get().trips, trip];
    set({ trips });
  },

  // Update trip locally
  updateTrip: (tripId, updates) => {
    const trips = get().trips.map(t => 
      t.trip_id === tripId ? { ...t, ...updates } : t
    );
    set({ trips });
  },

  // Remove trip locally
  removeTrip: (tripId) => {
    const trips = get().trips.filter(t => t.trip_id !== tripId);
    set({ trips });
  },

  // Get trip by ID
  getTripById: (tripId) => {
    return get().trips.find(t => t.trip_id === tripId);
  },
}));
